import React, { useEffect, useState } from 'react';
import { Search, MapPin, Clock, Star, X } from 'lucide-react';
import marketApi from './marketApi';
import { ratingText } from './ratingText';

const CATS = [
  { value: 'restaurant', emoji: '🍽️', label: 'Restaurants' },
  { value: 'supermarket', emoji: '🛒', label: 'Supermarchés' },
  { value: 'pharmacy', emoji: '💊', label: 'Pharmacies' },
  { value: 'bakery', emoji: '🥐', label: 'Boulangeries' },
  { value: 'cafe', emoji: '☕', label: 'Cafés' },
  { value: 'butcher', emoji: '🥩', label: 'Boucheries' },
  { value: 'shop', emoji: '🏬', label: 'Boutiques' },
  { value: 'electronics', emoji: '📱', label: 'Électronique' },
  { value: 'clothing', emoji: '👕', label: 'Habillement' },
  { value: 'other', emoji: '📦', label: 'Autres' },
];
const TYPE_LABEL = Object.fromEntries(CATS.map(c => [c.value, c.label.replace(/s$/, '')]));

// Les 5 autres verticales Mbiyo — chaque tuile a sa teinte propre (super-app).
const SERVICES = [
  { page: 'events',      emoji: '🎟️', label: 'Billetterie', tint: '#F3E8FF', ink: '#7C3AED' },
  { page: 'parcel-send', emoji: '📦', label: 'Colis',       tint: '#E0F2FE', ink: '#0369A1' },
  { page: 'lodgings',    emoji: '🏨', label: 'Hôtels',      tint: '#CCFBF1', ink: '#0F766E' },
  { page: 'beauty',      emoji: '💇', label: 'Beauté',      tint: '#FCE7F3', ink: '#BE185D' },
  { page: 'rentals',     emoji: '🚗', label: 'Location',    tint: '#FEF3C7', ink: '#B45309' },
  { page: 'venues',      emoji: '🎉', label: 'Salles',      tint: '#FFE4E6', ink: '#BE123C' },
];

// Fourchette « 25–35 min » (préparation + trajet) — même règle que le natif.
const etaRange = (eta) => {
  const base = Math.round((eta || 30) / 5) * 5;
  const lo = Math.max(5, base - 5);
  return `${lo}–${lo + 10} min`;
};
const km = (d) => (d < 1 ? `${Math.round(d * 1000)} m` : `${d.toLocaleString('fr-FR')} km`);

function ProductCard({ p, onOpen, deal }) {
  return (
    <button className="mk-card" onClick={() => onOpen(p.supplier_id, p.product_id)}>
      <div className="mk-card-imgwrap">
        {p.image
          ? <img className="mk-card-img" src={p.image_thumb || p.image} alt="" loading="lazy" />
          : <div className="mk-card-img mk-card-ph">🍽️</div>}
        {p.eta_min != null && <span className="mk-eta"><Clock size={11} /> {etaRange(p.eta_min)}</span>}
        {deal && p.discount_pct > 0 && <span className="mk-flag">-{p.discount_pct}%</span>}
      </div>
      <span className="mk-card-title">{p.name}</span>
      <span className="mk-card-price">
        {((p.promo_price || p.price) || 0).toLocaleString('fr-FR')} FC
        {p.promo_price ? <span className="mk-card-old"> {(p.price || 0).toLocaleString('fr-FR')} FC</span> : null}
      </span>
      {p.distance_km != null && <span className="mk-meta"><MapPin size={11} /> <b>{km(p.distance_km)}</b></span>}
    </button>
  );
}

function SupplierCard({ s, onOpen, isNew }) {
  const type = TYPE_LABEL[s.business_type];
  return (
    <button className={`mk-card mk-sup${s.is_open ? '' : ' closed'}`} onClick={() => onOpen(s.supplier_id)}>
      <div className="mk-card-imgwrap">
        {s.logo
          ? <img className="mk-card-img" src={s.logo_thumb || s.logo} alt="" loading="lazy" />
          : <div className="mk-card-img mk-card-ph">🏪</div>}
        <span className={`mk-dotchip ${s.is_open ? 'on' : 'off'}`}><i />{s.is_open ? 'Ouvert' : 'Fermé'}</span>
        {isNew && <span className="mk-flag new">Nouveau</span>}
        {!isNew && s.has_promo && <span className="mk-flag promo">🏷️ Promo</span>}
        {s.business_type === 'pharmacy' && s.on_duty && <span className="mk-eta" style={{ left: 'auto', right: 6, background: '#1E1B4B' }}>🌙 De garde</span>}
        {s.eta_min != null && s.is_open && <span className="mk-eta"><Clock size={11} /> {etaRange(s.eta_min)}</span>}
      </div>
      <span className="mk-card-title">{s.business_name}</span>
      <span className="mk-meta">
        <Star size={11} fill="#FFB900" color="#FFB900" /> {ratingText(s.rating, s.rating_count)}
        {type && <> · {type}</>}
        {s.distance_km != null && <> · <b>{km(s.distance_km)}</b></>}
      </span>
    </button>
  );
}

// En-tête de section avec sous-titre optionnel ; masquée si vide.
function Section({ title, sub, children, count, className }) {
  if (!count) return null;
  return (
    <section className={`mk-section${className ? ` ${className}` : ''}`}>
      <div className="mk-section-head">
        <div>
          <h2>{title}</h2>
          {sub && <p className="mkh-sub">{sub}</p>}
        </div>
      </div>
      <div className="mk-row">{children}</div>
    </section>
  );
}

// Squelettes de chargement (pas de spinner : la page dessine sa structure).
function SkeletonHome() {
  return (
    <div aria-hidden="true">
      {[0, 1].map(i => (
        <section className="mk-section" key={i}>
          <div className="mkh-skel line" style={{ width: i ? 150 : 210 }} />
          <div className="mk-row">
            {[...Array(5)].map((_, j) => (
              <div className="mkh-skel-card" key={j}>
                <div className="mkh-skel img" />
                <div className="mkh-skel line" />
                <div className="mkh-skel line" style={{ width: '55%' }} />
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export default function MarketHome({ nav, citySlug, isAuthenticated, currentUser }) {
  const [loading, setLoading] = useState(true);
  const [pos, setPos] = useState(null); // { lat, lng } — géoloc navigateur (silencieuse)
  const [popular, setPopular] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [nearby, setNearby] = useState([]);
  const [deals, setDeals] = useState([]);
  const [reorder, setReorder] = useState([]);
  const [news, setNews] = useState([]);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState(null); // recherche fournisseurs+produits
  const [activeCat, setActiveCat] = useState(null);
  const [catSuppliers, setCatSuppliers] = useState(null);
  const [pharmaFilter, setPharmaFilter] = useState('all'); // pharmacies : all | open | on_duty

  // Géolocalisation silencieuse (pas de blocage si refus).
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => setPos({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => {},
      { enableHighAccuracy: false, timeout: 6000, maximumAge: 300000 }
    );
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const posQ = pos ? { lat: pos.lat, lng: pos.lng } : {};
      const cityQ = citySlug ? { city_slug: citySlug } : {};
      const [pop, sup, nb, dl, nw, ro] = await Promise.all([
        marketApi.productsPopular({ limit: 30, ...cityQ, ...posQ }).catch(() => null),
        marketApi.suppliersPopular({ limit: 20, ...cityQ }).catch(() => null),
        pos ? marketApi.suppliersNearby({ ...posQ, type: 'restaurant,cafe,supermarket,bakery', open_only: 1, limit: 12, radius: 15, ...cityQ }).catch(() => null) : null,
        marketApi.productsDeals({ limit: 12, ...cityQ, ...posQ }).catch(() => null),
        marketApi.suppliersNew({ limit: 10, ...cityQ }).catch(() => null),
        isAuthenticated ? marketApi.productsReorder({ limit: 12, ...posQ }).catch(() => null) : null,
      ]);
      if (!alive) return;
      setPopular(pop?.products || []);
      setSuppliers(sup?.suppliers || []);
      setNearby(nb?.suppliers || []);
      setDeals(dl?.products || []);
      setNews(nw?.suppliers || []);
      setReorder(ro?.products || []);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [citySlug, pos, isAuthenticated]);

  // Recherche globale (produits + fournisseurs par nom)
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setResults(null); return; }
    const t = setTimeout(async () => {
      const [pr, sr] = await Promise.all([
        marketApi.productsSearch({ q, limit: 20, ...(citySlug ? { city_slug: citySlug } : {}) }).catch(() => null),
        marketApi.suppliers({ search: q, limit: 12, ...(citySlug ? { city_slug: citySlug } : {}) }).catch(() => null),
      ]);
      setResults({ products: pr?.products || [], suppliers: sr?.suppliers || [] });
    }, 300);
    return () => clearTimeout(t);
  }, [search, citySlug]);

  // Filtre par type de commerce
  useEffect(() => {
    if (!activeCat) { setCatSuppliers(null); return; }
    let alive = true;
    (async () => {
      const r = await marketApi.suppliers({
        type: activeCat, limit: 40, ...(citySlug ? { city_slug: citySlug } : {}),
        ...(activeCat === 'pharmacy' && pharmaFilter === 'on_duty' ? { on_duty: 1 } : {}),
      }).catch(() => null);
      if (alive) setCatSuppliers(r?.suppliers || []);
    })();
    return () => { alive = false; };
  }, [activeCat, citySlug, pharmaFilter]);

  const openStore = (id) => nav({ page: 'store', supplierId: id });
  const openStoreProduct = (sid) => nav({ page: 'store', supplierId: sid });

  const firstName = (currentUser?.name || '').trim().split(/\s+/)[0] || null;
  const cityLabel = citySlug ? citySlug.charAt(0).toUpperCase() + citySlug.slice(1) : null;
  const maxDeal = deals.reduce((m, d) => Math.max(m, d.discount_pct || 0), 0);

  return (
    <div className="mk-container">
      {/* ── Accueil : salutation + recherche ── */}
      <header className="mkh-hero">
        <p className="mkh-hello">Bonjour{firstName ? ` ${firstName}` : ''} 👋</p>
        <h1>Qu'est-ce qui vous ferait <span className="fire">plaisir</span> ?</h1>
        <span className="mkh-loc">
          <MapPin size={12} /> Livraison {cityLabel ? `à ${cityLabel}` : pos ? 'autour de vous' : 'dans votre ville'}
        </span>
        <div className="mk-search mkh-search">
          <Search size={19} color="var(--mb-text-3)" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Un plat, un produit, un commerce…"
          />
          {search && (
            <button className="mkh-clear" onClick={() => setSearch('')} aria-label="Effacer">
              <X size={15} />
            </button>
          )}
        </div>
      </header>

      {/* ── Les autres services Mbiyo ── */}
      <section className="mk-section" style={{ marginTop: 20 }}>
        <div className="mkh-services">
          {SERVICES.map(s => (
            <button
              key={s.page}
              className="mkh-service"
              style={{ '--tint': s.tint, '--ink': s.ink }}
              onClick={() => nav({ page: s.page })}
            >
              <span className="ico">{s.emoji}</span>
              <span>{s.label}</span>
            </button>
          ))}
        </div>
      </section>

      {/* ── Types de commerces (pilules) ── */}
      <section className="mk-section" style={{ marginTop: 16 }}>
        <div className="mkh-cats">
          {CATS.map(c => (
            <button
              key={c.value}
              className={`mkh-pill${activeCat === c.value ? ' active' : ''}`}
              onClick={() => setActiveCat(activeCat === c.value ? null : c.value)}
            >
              <span>{c.emoji}</span>{c.label}
            </button>
          ))}
        </div>
      </section>

      {loading && !results && !catSuppliers && <SkeletonHome />}

      {/* Résultats de recherche */}
      {results && (
        <>
          <Section title={`Commerces (${results.suppliers.length})`} count={results.suppliers.length} className="mkh-grid-section">
            {results.suppliers.map(s => <SupplierCard key={s.supplier_id} s={s} onOpen={openStore} />)}
          </Section>
          <Section title={`Produits (${results.products.length})`} count={results.products.length} className="mkh-grid-section">
            {results.products.map(p => <ProductCard key={p.product_id} p={p} onOpen={openStoreProduct} />)}
          </Section>
          {!results.suppliers.length && !results.products.length && (
            <div className="mkh-empty">
              <span className="big">🔎</span>
              <b>Aucun résultat pour « {search} »</b>
              <p>Essayez un autre mot, ou parcourez les commerces ci-dessous.</p>
            </div>
          )}
        </>
      )}

      {/* Filtre catégorie */}
      {!results && catSuppliers && (
        <section className="mk-section mkh-grid-section">
          <div className="mk-section-head">
            <div><h2>{CATS.find(c => c.value === activeCat)?.label}</h2></div>
            <button className="mk-seeall" onClick={() => setActiveCat(null)}>Tout afficher</button>
          </div>
          {activeCat === 'pharmacy' && (
            <div className="mkh-cats" style={{ marginBottom: 10 }}>
              {[['all', 'Toutes'], ['open', 'Ouvertes maintenant'], ['on_duty', '🌙 De garde']].map(([k, l]) => (
                <button key={k} className={`mkh-pill${pharmaFilter === k ? ' active' : ''}`} onClick={() => setPharmaFilter(k)}>{l}</button>
              ))}
            </div>
          )}
          {catSuppliers.length
            ? <div className="mk-row">{(activeCat === 'pharmacy' && pharmaFilter === 'open' ? catSuppliers.filter(x => x.is_open) : catSuppliers).map(s => <SupplierCard key={s.supplier_id} s={s} onOpen={openStore} />)}</div>
            : (
              <div className="mkh-empty">
                <span className="big">{CATS.find(c => c.value === activeCat)?.emoji}</span>
                <b>Aucun commerce de ce type pour le moment</b>
                <p>Ils arrivent bientôt sur Mbiyo — revenez voir !</p>
              </div>
            )}
        </section>
      )}

      {/* Sections découverte (identiques au natif) */}
      {!results && !catSuppliers && !loading && (
        <>
          <Section title="Les plus proches de vous" sub="Ouverts maintenant, autour de vous" count={nearby.length}>
            {nearby.map(s => <SupplierCard key={s.supplier_id} s={s} onOpen={openStore} />)}
          </Section>
          <Section title="Commandez à nouveau" sub="Vos habitudes, en un geste" count={reorder.length}>
            {reorder.map(p => <ProductCard key={p.product_id} p={p} onOpen={openStoreProduct} />)}
          </Section>
          {deals.length > 0 && (
            <section className="mk-section mkh-deals">
              <div className="mk-section-head">
                <div>
                  <h2>🔥 Bons plans</h2>
                  <p className="mkh-sub">{maxDeal > 0 ? `Jusqu'à -${maxDeal}% en ce moment` : 'Promotions en cours'}</p>
                </div>
              </div>
              <div className="mk-row">
                {deals.map(p => <ProductCard key={p.product_id} p={p} onOpen={openStoreProduct} deal />)}
              </div>
            </section>
          )}
          <Section title="Les plus achetés" sub="Ce que votre ville commande le plus" count={popular.length}>
            {popular.map(p => <ProductCard key={p.product_id} p={p} onOpen={openStoreProduct} />)}
          </Section>
          <Section title="✨ Nouveaux sur Mbiyo" count={news.length}>
            {news.map(s => <SupplierCard key={s.supplier_id} s={s} onOpen={openStore} isNew />)}
          </Section>
          <Section title="Commerces populaires" count={suppliers.length}>
            {suppliers.map(s => <SupplierCard key={s.supplier_id} s={s} onOpen={openStore} />)}
          </Section>
          {!nearby.length && !popular.length && !suppliers.length && (
            <div className="mkh-empty">
              <span className="big">🛍️</span>
              <b>Le marché ouvre bientôt dans votre ville</b>
              <p>Les premiers commerces Mbiyo arrivent — en attendant, les courses et les colis vous attendent.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
